/**
 * Premium Session Recording — shared contract for the web app and the Lambdas.
 *
 * Whole in-person session audio (native recorder, consent captured on the
 * provider's device) → resumable multipart upload to a short-lived KMS bucket
 * → Amazon Transcribe batch (en-AU, speaker labels) → compact transcript →
 * Bedrock (Claude Sonnet 4.6, AU profile) two-pass draft of AHP's "Session
 * notes" (`ahp-note-v1`, see `ahp-note.ts`) → provider reviews, edits,
 * confirms the suggestions and approves. Approval deletes
 * the audio; unapproved audio is deleted after 7 days. The compact transcript
 * and the note are kept as the record, and so is a spoken-consent clip until
 * consent is withdrawn, the recording is discarded or the member's account is
 * deleted.
 *
 * Access: the admin "Premium Session Recording" switch AND `canUpdateSession`
 * (assigned provider or L4), enforced on every provider route. Members only
 * ever see the content-free status view.
 *
 * Everything here is content-free metadata or typed shapes; nothing in this
 * module may be logged with user content.
 */

import type { AhpNote, AhpNoteEdits, AhpService } from './ahp-note'
import type { ProgressNoteDraftFields } from './progress-note'
import type { Session, SessionLifecycleState } from './types'

// --- Limits & constants ------------------------------------------------------

/** Hard capture cap (owner decision 5: 100 minutes). */
export const SESSION_RECORDING_MAX_SECONDS = 100 * 60
/** Warn the provider this long before the cap. */
export const SESSION_RECORDING_CAP_WARNING_SECONDS = 5 * 60
/** Recordings a provider may start per rolling 24 hours (spend bound). */
export const SESSION_RECORDING_DAILY_CAP = 10
/** Recordings per session (a restart after a mistake is fine; a loop is not). */
export const SESSION_RECORDING_MAX_PER_SESSION = 3
/** Note regenerations per recording (Bedrock spend bound). */
export const SESSION_RECORDING_REGENERATE_LIMIT = 3
/** Manual retries of a failed transcription / drafting step per recording (Transcribe + Bedrock spend bound). */
export const SESSION_RECORDING_RETRY_LIMIT = 3
/** Unapproved audio is deleted this long after capture (owner decision 3). */
export const SESSION_RECORDING_AUDIO_MAX_DAYS = 7
/**
 * Bucket lifecycle backstop for session audio and raw Transcribe output —
 * they expire regardless of state. Objects are written after capture starts,
 * so this never cuts the 7-day audio window short. Consent clips are exempt.
 */
export const SESSION_RECORDING_BUCKET_EXPIRY_DAYS = 7
/**
 * A capture whose row has said "recording" for longer than the cap plus this
 * grace is treated as no longer live (app killed / never stopped): it stops
 * blocking a new recording and the member banner stops saying "recording".
 */
export const SESSION_RECORDING_LIVE_GRACE_SECONDS = 15 * 60
/** Speaker labels requested from Transcribe. */
export const SESSION_RECORDING_MAX_SPEAKERS = 4
/** The one upload format: AAC-LC in an MPEG-4 (.m4a) container. */
export const SESSION_RECORDING_CONTENT_TYPE = 'audio/mp4'

/**
 * Capture encoding. A single constant on purpose: final tuning (32 vs 48 kbps)
 * waits for the owner's 100-minute device test — change it here only.
 */
export const SESSION_RECORDING_AUDIO_FORMAT = {
  codec: 'aac-lc',
  container: 'm4a',
  sampleRate: 16_000,
  channels: 1,
  bitrate: 32_000,
  segmentSeconds: 60,
} as const

/**
 * Upload byte cap derived from the capture encoding so a client that lies
 * about `durationSec` cannot store ~250 min of audio. 32 kbps × (100 min + one
 * 60 s flush segment) × 1.25 container overhead ≈ 29 MiB.
 */
export const SESSION_RECORDING_MAX_BYTES = Math.ceil(
  (SESSION_RECORDING_AUDIO_FORMAT.bitrate / 8) *
    (SESSION_RECORDING_MAX_SECONDS + SESSION_RECORDING_AUDIO_FORMAT.segmentSeconds) *
    1.25,
)
/** On-device byte budget: the recorder stops here, leaving 1 MiB under the upload cap for the join and flush. */
export const SESSION_RECORDING_LOCAL_MAX_BYTES = SESSION_RECORDING_MAX_BYTES - 1024 * 1024
/** Multipart part size (S3 minimum for every part but the last). */
export const SESSION_RECORDING_PART_SIZE_BYTES = 5 * 1024 * 1024

/**
 * Every in-person recording notice version the create route accepts (stored
 * exactly as sent). Installed Android ≤ 131 / iOS ≤ 96 builds show v1;
 * Android 132 / iOS 97 show v2.
 */
export const SESSION_RECORDING_NOTICE_VERSIONS = [
  'session-recording-notice-v1',
  'session-recording-notice-v2',
  'session-recording-notice-v3',
] as const
export type SessionRecordingNoticeVersion = (typeof SESSION_RECORDING_NOTICE_VERSIONS)[number]
/** The notice the consent sheet shows now; new consents carry this version. */
export const SESSION_RECORDING_NOTICE_VERSION =
  'session-recording-notice-v3' satisfies SessionRecordingNoticeVersion

export function isSessionRecordingNoticeVersion(raw: unknown): raw is SessionRecordingNoticeVersion {
  return (SESSION_RECORDING_NOTICE_VERSIONS as readonly unknown[]).includes(raw)
}
/**
 * Bump when the note prompts change (stored on every draft). `ahp-note-v1`
 * drafts carry `note.ahp`; `therapy-note-v2` drafts (before it) don't.
 */
export const SESSION_RECORDING_PROMPT_VERSION = 'ahp-note-v1'
/** Bedrock model: Claude Sonnet 4.6 via the Australian inference profile (owner decision 8). */
export const SESSION_RECORDING_BEDROCK_MODEL_ID = 'au.anthropic.claude-sonnet-4-6'

/** Transcribe job names start with this so the EventBridge rule only sees ours. */
export const SESSION_RECORDING_TRANSCRIBE_JOB_PREFIX = 'srec-'
/** S3 key prefixes inside the session-audio bucket. */
export const SESSION_RECORDING_AUDIO_PREFIX = 'audio/'
export const SESSION_RECORDING_TRANSCRIBE_OUTPUT_PREFIX = 'transcribe-out/'
/**
 * Spoken-consent clips: uploaded under `consent-pending/` (expires after a
 * day), moved to `consent/` once a recording references them. `consent/` has
 * no lifecycle expiry — approval and the 7-day sweep never touch it; withdraw,
 * discard and the member's account deletion do.
 */
export const SESSION_RECORDING_CONSENT_PENDING_PREFIX = 'consent-pending/'
export const SESSION_RECORDING_CONSENT_CLIP_PREFIX = 'consent/'
export const SESSION_RECORDING_CONSENT_PENDING_EXPIRY_DAYS = 1

/** FCM data `type` for the content-free "draft ready" push. */
export const SESSION_RECORDING_DRAFT_READY_PUSH_TYPE = 'session_recording_draft_ready'

export function sessionRecordingAudioKey(sessionUuid: string, recordingId: string): string {
  return `${SESSION_RECORDING_AUDIO_PREFIX}${sessionUuid}/${recordingId}.m4a`
}

/** Where an uploaded clip waits until the create route attaches it (scoped to the uploader). */
export function sessionRecordingConsentPendingKey(sessionUuid: string, uploaderSub: string, clipId: string): string {
  return `${SESSION_RECORDING_CONSENT_PENDING_PREFIX}${sessionUuid}/${uploaderSub}/${clipId}`
}

/** A clip kept as the consent record of one recording. */
export function sessionRecordingConsentClipKey(sessionUuid: string, recordingId: string, clipId: string): string {
  return `${SESSION_RECORDING_CONSENT_CLIP_PREFIX}${sessionUuid}/${recordingId}/${clipId}`
}

/** Inverse of {@link sessionRecordingAudioKey} (also accepts an `s3://bucket/key` URI). */
export function parseSessionRecordingAudioKey(
  keyOrUri: string,
): { sessionUuid: string; recordingId: string } | null {
  const key = keyOrUri.replace(/^s3:\/\/[^/]+\//, '')
  const escaped = SESSION_RECORDING_AUDIO_PREFIX.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&')
  const match = new RegExp(`^${escaped}([^/]+)/([^/]+)\\.m4a$`).exec(key)
  return match ? { sessionUuid: match[1], recordingId: match[2] } : null
}

export function sessionRecordingTranscribeOutputKey(
  sessionUuid: string,
  recordingId: string,
  attempt: number,
): string {
  return `${SESSION_RECORDING_TRANSCRIBE_OUTPUT_PREFIX}${sessionUuid}/${recordingId}-${attempt}.json`
}

/** Transcribe job name: prefix + recording id + attempt (≤ 200 chars, [0-9a-zA-Z._-]). */
export function sessionRecordingTranscribeJobName(recordingId: string, attempt: number): string {
  const safeId = recordingId.replace(/[^a-zA-Z0-9_-]/g, '-')
  return `${SESSION_RECORDING_TRANSCRIBE_JOB_PREFIX}${safeId}-${attempt}`.slice(0, 200)
}

// --- Status machine ------------------------------------------------------------

/**
 * `recording → uploading → uploaded → transcribing → drafting → draft_ready → approved`
 * plus terminal `failed` (see {@link SessionRecordingFailure}), `withdrawn`
 * (consent withdrawn before approval — recordings, transcript and draft deleted;
 * a content-free record stays) and `discarded` (provider discarded before
 * approval — the same).
 */
export type SessionRecordingStatus =
  | 'recording'
  | 'uploading'
  | 'uploaded'
  | 'transcribing'
  | 'drafting'
  | 'draft_ready'
  | 'approved'
  | 'failed'
  | 'withdrawn'
  | 'discarded'

/** Device capture state, reported through the events route. */
export type SessionRecordingCaptureState = 'recording' | 'paused' | 'stopped'

export type SessionRecordingFailureStage = 'upload' | 'transcription' | 'drafting'

export interface SessionRecordingFailure {
  stage: SessionRecordingFailureStage
  /** Machine code, e.g. `NO_SPEECH`, `TRANSCRIBE_FAILED`, `BEDROCK_FAILED`, `UPLOAD_NEVER_COMPLETED`. */
  code: string
  /** True when `POST …/retry` can re-run the stage (audio or transcript still present). */
  retryable: boolean
  at: string
}

export type SessionRecordingAudioState = 'pending' | 'present' | 'deleted'
export type SessionRecordingAudioDeleteReason =
  | 'approved'
  | 'expired'
  | 'discarded'
  | 'withdrawn'
  | 'upload_abandoned'

export interface SessionRecordingAudioStatus {
  state: SessionRecordingAudioState
  /** When unapproved audio will be deleted automatically (capture + 7 days). */
  deleteDueAt: string | null
  deletedAt: string | null
  deleteReason: SessionRecordingAudioDeleteReason | null
}

const TERMINAL_STATUSES: readonly SessionRecordingStatus[] = [
  'approved',
  'failed',
  'withdrawn',
  'discarded',
]

/** True once nothing will change the recording without a provider action. */
export function isTerminalSessionRecordingStatus(status: SessionRecordingStatus): boolean {
  return TERMINAL_STATUSES.includes(status)
}

/** True while the server pipeline is working (poll while true). */
export function isSessionRecordingPipelineBusy(status: SessionRecordingStatus): boolean {
  return status === 'uploaded' || status === 'transcribing' || status === 'drafting'
}

/** Status values from which the provider may still discard / withdraw. */
export function canDiscardSessionRecording(status: SessionRecordingStatus): boolean {
  return status !== 'approved' && status !== 'withdrawn' && status !== 'discarded'
}

/**
 * True while a capture counts as live: status `recording`, not stopped, and
 * started within the cap + grace window.
 */
export function isSessionRecordingCaptureLive(
  record: { status: SessionRecordingStatus; captureState: SessionRecordingCaptureState; startedAt: string },
  nowMs: number,
): boolean {
  if (record.status !== 'recording' || record.captureState === 'stopped') return false
  const startedMs = Date.parse(record.startedAt)
  if (!Number.isFinite(startedMs)) return false
  return (
    nowMs - startedMs <=
    (SESSION_RECORDING_MAX_SECONDS + SESSION_RECORDING_LIVE_GRACE_SECONDS) * 1000
  )
}

// --- Session eligibility ---------------------------------------------------------

/** Lifecycle states in which the session is "in progress" (checked in, not out). */
export const SESSION_RECORDING_IN_PROGRESS_STATES: readonly SessionLifecycleState[] = [
  'checked-in',
  'tracking',
  'checkout-reminded',
]

export type SessionRecordingIneligibleReason = 'not_in_person' | 'not_in_progress'

/** In-person sessions that are in progress only (telehealth uses call recording — Phase 2). */
export function sessionRecordingIneligibleReason(
  session: Pick<Session, 'faceToFace' | 'lifecycleState'>,
): SessionRecordingIneligibleReason | null {
  if (session.faceToFace !== true) return 'not_in_person'
  if (!SESSION_RECORDING_IN_PROGRESS_STATES.includes(session.lifecycleState)) {
    return 'not_in_progress'
  }
  return null
}

// --- Consent ---------------------------------------------------------------------

export type SessionRecordingPersonRole =
  | 'participant'
  | 'guardian'
  | 'nominee'
  | 'carer'
  | 'support_worker'
  | 'interpreter'
  | 'family'
  | 'other'

export const SESSION_RECORDING_PERSON_ROLES: readonly SessionRecordingPersonRole[] = [
  'participant',
  'guardian',
  'nominee',
  'carer',
  'support_worker',
  'interpreter',
  'family',
  'other',
]

/** Who agreed to the recording for the participant. */
export type SessionRecordingConsenter =
  | 'participant'
  | 'guardian'
  | 'nominee'
  /** Someone a Provider Owner marked "Can give consent for {member}", agreeing in their own app. */
  | 'authorised_person'

export function isSessionRecordingConsenter(raw: unknown): raw is SessionRecordingConsenter {
  return raw === 'participant' || raw === 'guardian' || raw === 'nominee' || raw === 'authorised_person'
}

export interface SessionRecordingPersonPresent {
  role: SessionRecordingPersonRole
  /** First name only (optional, ≤ 40 chars). Never logged. */
  firstName?: string | null
}

/** Easy Read card wording above the "Yes, record" / "No" buttons. */
export interface SessionRecordingNotice {
  heading: string
  lines: string[]
}

/**
 * Easy Read card wording, per notice version, as the participant saw it. A
 * consent record is only meaningful against the exact wording, so shipped
 * wording is never edited: new wording gets a new version.
 */
const NOTICES: Record<SessionRecordingNoticeVersion, (therapist: string) => SessionRecordingNotice> = {
  'session-recording-notice-v1': () => ({
    heading: 'Can I record our session?',
    lines: [
      'It helps me write my notes.',
      'A computer turns the talk into text, then the sound recording is deleted.',
      'You can say stop at any time.',
    ],
  }),
  'session-recording-notice-v2': (therapist) => ({
    heading: 'Can I record our session?',
    lines: [
      'It helps me write my notes.',
      'A computer turns the talk into text.',
      'A computer helps write the notes.',
      `${therapist} checks the notes.`,
      'Then the sound recording is deleted.',
      'You can say stop at any time.',
    ],
  }),
  'session-recording-notice-v3': (therapist) => ({
    heading: 'Can I record our session?',
    lines: [
      'It helps me write my notes.',
      'A computer turns the talk into text.',
      'A computer helps write the notes.',
      `${therapist} checks the notes.`,
      'Then the sound recording is deleted.',
      'You can say stop at any time.',
      'Then we delete the recordings.',
      'Notes we already checked stay in your file.',
    ],
  }),
}

/** `therapist` is how the card names the recording provider (first name, or "Your therapist"). */
export function sessionRecordingNotice(
  version: SessionRecordingNoticeVersion,
  therapist: string,
): SessionRecordingNotice {
  return NOTICES[version](therapist)
}

// --- Consent methods ---------------------------------------------------------------

/**
 * How agreement was shown, stored in its own field (the notice version only
 * says which card wording they saw). Each satisfies consent for this one
 * session whatever the member has or hasn't accepted in their own app.
 * In person, on the provider's phone:
 * - `participant_tap` — tapped "Yes, record" on the Easy Read card.
 * - `verbal_clip` — said the consent phrase; the short clip is kept as the consent record.
 * - `nonverbal` — the participant showed they agree without words (nod, gesture, AAC…).
 * In someone else's app:
 * - `guardian_app` — a person a Provider Owner marked "Can give consent for
 *   {member}" accepted a consent request in their own app.
 */
export type SessionRecordingConsentMethod = 'participant_tap' | 'verbal_clip' | 'nonverbal' | 'guardian_app'

export const SESSION_RECORDING_CONSENT_METHODS: readonly SessionRecordingConsentMethod[] = [
  'participant_tap',
  'verbal_clip',
  'nonverbal',
  'guardian_app',
]

// --- Consent requests (Ask a parent/guardian or support coordinator in their app) ----------

/** How long the person has to answer; the provider can send it again after. */
export const SESSION_RECORDING_CONSENT_REQUEST_TTL_SECONDS = 10 * 60
/** Minimum gap between two requests to the same person for the same session. */
export const SESSION_RECORDING_CONSENT_REQUEST_RESEND_COOLDOWN_SECONDS = 60
/** Requests to one person per session (resends included). */
export const SESSION_RECORDING_CONSENT_REQUEST_MAX_PER_PERSON = 5
/** FCM data `type` (a `SESSION_*` type: Android lock-screen PRIVATE, one in-app history row). */
export const SESSION_RECORDING_CONSENT_REQUEST_PUSH_TYPE = 'SESSION_RECORDING_CONSENT_REQUEST'

export type SessionRecordingConsentRequestStatus = 'pending' | 'accepted' | 'declined' | 'expired'

/** Someone who can be asked in their app (the member's association, marked by a Provider Owner). */
export interface SessionRecordingConsentContact {
  userId: string
  firstName: string
  relationship: string | null
  relationshipName: string | null
  /** A Provider Owner set them as the member's parent or guardian (may agree for someone under 18). */
  parentOrGuardian: boolean
}

/** The provider's view of one request (polled while waiting). */
export interface SessionRecordingConsentRequestSummary {
  requestId: string
  recipientUserId: string
  recipientFirstName: string
  relationship: string | null
  relationshipName: string | null
  status: SessionRecordingConsentRequestStatus
  createdAt: string
  expiresAt: string
  respondedAt: string | null
  /** An accepted request that already backs a recording — ask again for another. */
  used: boolean
}

/** `GET …/recordings/consent-requests` (provider). */
export interface SessionRecordingConsentRequestList {
  contacts: SessionRecordingConsentContact[]
  requests: SessionRecordingConsentRequestSummary[]
}

/** `GET …/recordings/consent-requests?requestId=` for the person asked: what their accept screen shows. */
export interface SessionRecordingConsentRequestForRecipient {
  requestId: string
  sessionUuid: string
  status: SessionRecordingConsentRequestStatus
  expiresAt: string
  respondedAt: string | null
  noticeVersion: SessionRecordingNoticeVersion
  providerFirstName: string
  memberFirstName: string
  serviceType: string | null
  /** Session start, epoch ms. */
  sessionStart: number | null
  timezone: string | null
}

/** `POST …/recordings/consent-requests` — ask (or ask again). */
export interface CreateConsentRequestBody {
  recipientUserId: string
  /** The sheet's under-18 answer (from the date of birth when known): only a parent / guardian may then be asked. */
  under18: boolean
}

/**
 * AHP relationship values that PRE-SELECT "Parent or guardian" when a Provider
 * Owner marks someone "Can give consent" (`parent` is the legacy M1 value; AHP
 * has no `guardian` value yet). Only the Provider Owner's saved choice
 * (`SessionRecordingConsentContact.parentOrGuardian`) decides who may agree for
 * someone under 18.
 */
export const SESSION_RECORDING_PARENT_GUARDIAN_RELATIONSHIPS: ReadonlySet<string> = new Set(['parent', 'guardian'])

export function isParentOrGuardianRelationship(relationship: string | null | undefined): boolean {
  return SESSION_RECORDING_PARENT_GUARDIAN_RELATIONSHIPS.has((relationship ?? '').trim().toLowerCase())
}

export const SESSION_RECORDING_UNDER_18_APP_CONSENT_MESSAGE =
  'For someone under 18, only a parent or guardian can agree in their app.'

/** `POST …/recordings/consent-requests` — the person asked answers. */
export interface RespondConsentRequestBody {
  requestId: string
  decision: 'accept' | 'decline'
}

/**
 * The app agreement on a consent record. The client sends only `requestId`;
 * the server fills the rest from the accepted request (never trusted from the client).
 */
export interface SessionRecordingGuardianAppConsent {
  requestId: string
  recipientUserId?: string
  recipientFirstName?: string | null
  relationship?: string | null
  relationshipName?: string | null
  respondedAt?: string
  noticeVersion?: SessionRecordingNoticeVersion
}

const RELATIONSHIP_LABELS: Record<string, string> = {
  next_of_kin: 'next of kin',
  professional_contact: 'professional contact',
  parent: 'parent',
  support_coordinator: 'support coordinator',
  family_other: 'family',
  plan_manager: 'plan manager',
}

/** "Mum" (AHP's free-text name) or the relationship in plain words; null when unknown. */
export function consentRelationshipLabel(
  relationship: string | null | undefined,
  relationshipName?: string | null,
): string | null {
  const name = (relationshipName ?? '').trim()
  if (name) return name
  const key = (relationship ?? '').trim().toLowerCase()
  if (!key) return null
  return RELATIONSHIP_LABELS[key] ?? key.replace(/_/g, ' ')
}

export function isSessionRecordingConsentMethod(raw: unknown): raw is SessionRecordingConsentMethod {
  return (SESSION_RECORDING_CONSENT_METHODS as readonly unknown[]).includes(raw)
}

/** How a participant showed agreement without words. */
export type SessionRecordingNonverbalSign =
  | 'nod'
  | 'gesture'
  | 'communication_device'
  | 'pointed_easy_read_card'
  | 'pointed_picture_card'
  | 'other'

export const SESSION_RECORDING_NONVERBAL_SIGNS: readonly SessionRecordingNonverbalSign[] = [
  'nod',
  'gesture',
  'communication_device',
  'pointed_easy_read_card',
  'pointed_picture_card',
  'other',
]

/** Provider-facing picker labels (also used, lower-cased, in the record statement). */
export const SESSION_RECORDING_NONVERBAL_SIGN_LABELS: Record<SessionRecordingNonverbalSign, string> = {
  nod: 'Nodded',
  gesture: 'Used a gesture or sign',
  communication_device: 'Used their communication device (AAC)',
  pointed_easy_read_card: 'Pointed to “Yes, record” on the Easy Read card',
  pointed_picture_card: 'Pointed to a picture card',
  other: 'Another way',
}

/** Why a child under 18 could not show their own agreement. */
export type SessionRecordingAssentUnableReason = 'too_young' | 'distressed' | 'communication' | 'other'

export const SESSION_RECORDING_ASSENT_UNABLE_REASONS: readonly SessionRecordingAssentUnableReason[] = [
  'too_young',
  'distressed',
  'communication',
  'other',
]

export const SESSION_RECORDING_ASSENT_UNABLE_LABELS: Record<SessionRecordingAssentUnableReason, string> = {
  too_young: 'Too young to understand',
  distressed: 'Upset, unsettled or asleep',
  communication: 'No reliable way to show yes or no yet',
  other: 'Another reason',
}

/**
 * Easy Read prompt read out before a spoken-consent clip, per version (never
 * edited once shipped). v1 is what Android 132 / iOS 97 show.
 */
export const SESSION_RECORDING_VERBAL_PROMPT_VERSIONS = [
  'session-recording-verbal-prompt-v1',
  'session-recording-verbal-prompt-v2',
] as const
export type SessionRecordingVerbalPromptVersion = (typeof SESSION_RECORDING_VERBAL_PROMPT_VERSIONS)[number]
export const SESSION_RECORDING_VERBAL_PROMPT_VERSION =
  'session-recording-verbal-prompt-v2' satisfies SessionRecordingVerbalPromptVersion

export function isSessionRecordingVerbalPromptVersion(raw: unknown): raw is SessionRecordingVerbalPromptVersion {
  return (SESSION_RECORDING_VERBAL_PROMPT_VERSIONS as readonly unknown[]).includes(raw)
}

/** Shown under the Easy Read card: `intro`, the `phrase` to say (large), then `lines`. */
export interface SessionRecordingVerbalPrompt {
  heading: string
  intro: string
  phrase: string
  lines: string[]
}

const VERBAL_PROMPTS: Record<SessionRecordingVerbalPromptVersion, SessionRecordingVerbalPrompt> = {
  'session-recording-verbal-prompt-v1': {
    heading: 'Say yes out loud',
    intro: 'If you agree, say:',
    phrase: 'I consent to recording.',
    lines: [
      'This phone records you saying it.',
      'We keep this short recording. It shows you said yes.',
      'If you change your mind, say stop. Then we delete it all.',
    ],
  },
  'session-recording-verbal-prompt-v2': {
    heading: 'Say yes out loud',
    intro: 'If you agree, say:',
    phrase: 'I consent to recording.',
    lines: [
      'This phone records you saying it.',
      'We keep this short recording. It shows you said yes.',
      'If you change your mind, say stop.',
      'Then we delete the recordings.',
      'Notes we already checked stay in your file.',
    ],
  },
}

export function sessionRecordingVerbalPrompt(version: SessionRecordingVerbalPromptVersion): SessionRecordingVerbalPrompt {
  return VERBAL_PROMPTS[version]
}

/** A spoken-consent clip is at most this long (the device stops recording here). */
export const SESSION_RECORDING_CONSENT_CLIP_MAX_SECONDS = 60
/** Upload cap for one clip (60 s even at a 128 kbps browser default is under 1 MiB). */
export const SESSION_RECORDING_CONSENT_CLIP_MAX_BYTES = 2 * 1024 * 1024
/** Containers the in-app recorder produces (iOS: MPEG-4 / AAC; Android WebView: WebM / Opus). */
export const SESSION_RECORDING_CONSENT_CLIP_CONTENT_TYPES = ['audio/mp4', 'audio/webm'] as const
export type SessionRecordingConsentClipContentType = (typeof SESSION_RECORDING_CONSENT_CLIP_CONTENT_TYPES)[number]

/** `audio/webm;codecs=opus` → `audio/webm`; null when the container isn't one we accept. */
export function normalizeConsentClipContentType(raw: unknown): SessionRecordingConsentClipContentType | null {
  if (typeof raw !== 'string') return null
  const base = raw.split(';')[0].trim().toLowerCase()
  return (SESSION_RECORDING_CONSENT_CLIP_CONTENT_TYPES as readonly string[]).includes(base)
    ? (base as SessionRecordingConsentClipContentType)
    : null
}

/** The spoken agreement: the uploaded clip, the prompt read out first, and the provider's check. */
export interface SessionRecordingVerbalConsent {
  /** Issued by `POST /sessions/{id}/recordings/consent-clip`; the clip is uploaded before create. */
  clipId: string
  promptVersion: SessionRecordingVerbalPromptVersion
  /** Seconds of audio in the clip (device-reported). */
  durationSec: number
  /** The provider heard them consent (the clip can be played back first). Must be `true`. */
  providerHeardConsent: true
}

export interface SessionRecordingNonverbalConsent {
  sign: SessionRecordingNonverbalSign
  /** What they did, required for `other` (≤ 80 chars). Never logged. */
  otherSign?: string | null
  /** Optional support person who helped them show it (≤ 60 chars). Never logged. */
  supportPersonName?: string | null
  /** The provider saw them show they agree. Must be `true`. */
  providerSawAgreement: true
}

export type SessionRecordingAgeSource = 'date_of_birth' | 'provider_answer'

/** Under 18 from the member's date of birth when we have it, otherwise the provider's answer. */
export interface SessionRecordingAgeCheck {
  under18: boolean
  source: SessionRecordingAgeSource
}

/** A child's own agreement (assent), alongside their parent/guardian's consent. */
export type SessionRecordingChildAssent =
  | { method: 'participant_tap'; at: string }
  | { method: 'verbal_clip'; at: string; verbal: SessionRecordingVerbalConsent }
  | { method: 'nonverbal'; at: string; nonverbal: SessionRecordingNonverbalConsent }
  | {
      method: 'unable'
      at: string
      unable: {
        reason: SessionRecordingAssentUnableReason
        /** Required for `other` (≤ 120 chars). Never logged. */
        detail?: string | null
      }
    }

export type SessionRecordingChildAssentMethod = SessionRecordingChildAssent['method']

/**
 * Per-session consent captured on the provider's device. Never pre-ticked,
 * never inferred from the call-recording policy, never auto-consented.
 */
export interface SessionRecordingConsentInput {
  /** The Easy Read card the participant was shown — stored exactly as sent. */
  noticeVersion: SessionRecordingNoticeVersion
  /** The participant, or a guardian/nominee on their behalf. */
  consentedBy: SessionRecordingConsenter
  method: SessionRecordingConsentMethod
  /** Device time they agreed (ISO): the tap, the end of the clip, or the provider's "I saw them agree". */
  participantAgreedAt: string
  /** The provider confirmed everyone present was told and agrees. Must be `true`. */
  providerConfirmedEveryoneAgreed: true
  providerConfirmedAt: string
  /** Everyone in the room, including the participant. */
  peoplePresent: SessionRecordingPersonPresent[]
  /** Exactly when `method` is `verbal_clip`. */
  verbal?: SessionRecordingVerbalConsent
  /** Exactly when `method` is `nonverbal` (the participant only). */
  nonverbal?: SessionRecordingNonverbalConsent
  /** Exactly when `method` is `guardian_app`. */
  guardianApp?: SessionRecordingGuardianAppConsent
  /** Sent by every build with consent methods; absent from older builds (no child rules then). */
  age?: SessionRecordingAgeCheck
  /** Exactly when `age.under18`. */
  childAssent?: SessionRecordingChildAssent
}

/** Stored with the recording (server time + who captured it). */
export interface SessionRecordingConsentRecord extends SessionRecordingConsentInput {
  recordedAt: string
  capturedBySub: string
}

export type SessionRecordingConsentClipPurpose = 'consent' | 'assent'

/** Every clip a consent references: the consenter's (`consent`) and a child's own yes (`assent`). */
export function sessionRecordingConsentClipRefs(
  consent: Pick<SessionRecordingConsentInput, 'verbal' | 'childAssent'>,
): Array<{ purpose: SessionRecordingConsentClipPurpose; verbal: SessionRecordingVerbalConsent }> {
  const refs: Array<{ purpose: SessionRecordingConsentClipPurpose; verbal: SessionRecordingVerbalConsent }> = []
  if (consent.verbal) refs.push({ purpose: 'consent', verbal: consent.verbal })
  if (consent.childAssent?.method === 'verbal_clip') refs.push({ purpose: 'assent', verbal: consent.childAssent.verbal })
  return refs
}

export type SessionRecordingConsentClipState = 'pending' | 'present' | 'deleted'
export type SessionRecordingConsentClipDeleteReason = 'withdrawn' | 'discarded' | 'account_deleted'

/** Content-free metadata for one stored consent clip (the audio itself is fetched separately). */
export interface SessionRecordingConsentClip {
  clipId: string
  purpose: SessionRecordingConsentClipPurpose
  durationSec: number
  sizeBytes: number
  contentType: SessionRecordingConsentClipContentType
  state: SessionRecordingConsentClipState
  deletedAt: string | null
  deleteReason: SessionRecordingConsentClipDeleteReason | null
}

/** Under 18 at `nowMs` from an ISO date of birth; null when unknown or unreadable. */
export function participantUnder18FromDateOfBirth(dateOfBirth: string | null | undefined, nowMs: number): boolean | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateOfBirth ?? '')
  if (!match) return null
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]
  const birth = Date.UTC(year, month - 1, day)
  if (!Number.isFinite(birth) || birth > nowMs) return null
  const now = new Date(nowMs)
  const eighteenth = Date.UTC(year + 18, month - 1, day)
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) < eighteenth
}

export const SESSION_RECORDING_MAX_PEOPLE_PRESENT = 10
export const SESSION_RECORDING_FIRST_NAME_MAX = 40
export const SESSION_RECORDING_NONVERBAL_OTHER_MAX = 80
export const SESSION_RECORDING_SUPPORT_PERSON_MAX = 60
export const SESSION_RECORDING_ASSENT_DETAIL_MAX = 120
/** Device clocks drift; reject consent stamps further than this from server time. */
export const SESSION_RECORDING_CONSENT_CLOCK_SKEW_MS = 15 * 60 * 1000

export type ConsentValidation =
  | { ok: true; value: SessionRecordingConsentInput }
  | { ok: false; error: string }

function cleanText(raw: unknown, max: number): string | null {
  if (raw == null) return null
  if (typeof raw !== 'string') return null
  const trimmed = raw.replace(/[\u0000-\u001f<>]/g, '').replace(/\s+/g, ' ').trim()
  if (!trimmed) return null
  return trimmed.slice(0, max)
}

function cleanFirstName(raw: unknown): string | null {
  return cleanText(raw, SESSION_RECORDING_FIRST_NAME_MAX)
}

function isIsoNear(raw: unknown, nowMs: number): raw is string {
  if (typeof raw !== 'string') return false
  const ms = Date.parse(raw)
  return Number.isFinite(ms) && Math.abs(nowMs - ms) <= SESSION_RECORDING_CONSENT_CLOCK_SKEW_MS
}

const CLIP_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

function validateVerbal(raw: unknown): SessionRecordingVerbalConsent | string {
  const input = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  if (typeof input.clipId !== 'string' || !CLIP_ID.test(input.clipId)) {
    return 'Record them saying they consent, then try again'
  }
  if (!isSessionRecordingVerbalPromptVersion(input.promptVersion)) return 'The spoken-consent prompt is not current'
  const durationSec = input.durationSec
  if (
    typeof durationSec !== 'number' ||
    !Number.isFinite(durationSec) ||
    durationSec < 1 ||
    durationSec > SESSION_RECORDING_CONSENT_CLIP_MAX_SECONDS + 5
  ) {
    return 'The recorded answer is too short or too long'
  }
  if (input.providerHeardConsent !== true) return 'Confirm you heard them say they consent'
  return {
    clipId: input.clipId,
    promptVersion: input.promptVersion,
    durationSec: Math.round(durationSec),
    providerHeardConsent: true,
  }
}

function validateNonverbal(raw: unknown): SessionRecordingNonverbalConsent | string {
  const input = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const sign = input.sign as SessionRecordingNonverbalSign
  if (!SESSION_RECORDING_NONVERBAL_SIGNS.includes(sign)) return 'Choose how they showed they agree'
  const otherSign = cleanText(input.otherSign, SESSION_RECORDING_NONVERBAL_OTHER_MAX)
  if (sign === 'other' && !otherSign) return 'Say briefly how they showed they agree'
  if (input.providerSawAgreement !== true) return 'Confirm you saw them show they agree'
  return {
    sign,
    otherSign: sign === 'other' ? otherSign : null,
    supportPersonName: cleanText(input.supportPersonName, SESSION_RECORDING_SUPPORT_PERSON_MAX),
    providerSawAgreement: true,
  }
}

function validateChildAssent(raw: unknown, nowMs: number): SessionRecordingChildAssent | string {
  const input = (raw && typeof raw === 'object' ? raw : null) as Record<string, unknown> | null
  if (!input) return 'Ask the child too, or record why they couldn’t show it'
  if (!isIsoNear(input.at, nowMs)) return 'Consent times are missing or out of range'
  const at = input.at as string
  switch (input.method) {
    case 'participant_tap':
      return { method: 'participant_tap', at }
    case 'verbal_clip': {
      const verbal = validateVerbal(input.verbal)
      return typeof verbal === 'string' ? verbal : { method: 'verbal_clip', at, verbal }
    }
    case 'nonverbal': {
      const nonverbal = validateNonverbal(input.nonverbal)
      return typeof nonverbal === 'string' ? nonverbal : { method: 'nonverbal', at, nonverbal }
    }
    case 'unable': {
      const unable = (input.unable && typeof input.unable === 'object' ? input.unable : {}) as Record<string, unknown>
      const reason = unable.reason as SessionRecordingAssentUnableReason
      if (!SESSION_RECORDING_ASSENT_UNABLE_REASONS.includes(reason)) return 'Say why the child couldn’t show it'
      const detail = cleanText(unable.detail, SESSION_RECORDING_ASSENT_DETAIL_MAX)
      if (reason === 'other' && !detail) return 'Say briefly why the child couldn’t show it'
      return { method: 'unable', at, unable: { reason, detail: reason === 'other' ? detail : null } }
    }
    default:
      return 'Ask the child too, or record why they couldn’t show it'
  }
}

/** One person present (role + cleaned optional first name), validated like the consent list; null if invalid. */
export function validateSessionRecordingPersonPresent(raw: unknown): SessionRecordingPersonPresent | null {
  const role = (raw as { role?: unknown } | null)?.role
  if (!SESSION_RECORDING_PERSON_ROLES.includes(role as SessionRecordingPersonRole)) return null
  return {
    role: role as SessionRecordingPersonRole,
    firstName: cleanFirstName((raw as { firstName?: unknown }).firstName),
  }
}

/** Shared by the consent sheet (pre-flight) and the create route (authoritative). */
export function validateSessionRecordingConsent(raw: unknown, nowMs: number): ConsentValidation {
  if (!raw || typeof raw !== 'object') return { ok: false, error: 'Consent is required' }
  const input = raw as Record<string, unknown>
  const noticeVersion = input.noticeVersion
  if (!isSessionRecordingNoticeVersion(noticeVersion)) {
    return { ok: false, error: 'Consent notice version is not current' }
  }
  const method = input.method
  if (!isSessionRecordingConsentMethod(method)) {
    return {
      ok: false,
      error: 'Choose how they agreed: tap, say it out loud, show it without words, or ask in their app',
    }
  }
  if (input.providerConfirmedEveryoneAgreed !== true) {
    return { ok: false, error: 'Confirm that everyone present was told and agrees' }
  }
  const consentedBy = input.consentedBy
  if (!isSessionRecordingConsenter(consentedBy)) {
    return { ok: false, error: 'Say who agreed to the recording' }
  }
  // An app agreement comes only from an authorised person, and they agree only that way.
  if ((method === 'guardian_app') !== (consentedBy === 'authorised_person')) {
    return { ok: false, error: 'Ask the person who can give consent in their app' }
  }
  if (!isIsoNear(input.participantAgreedAt, nowMs) || !isIsoNear(input.providerConfirmedAt, nowMs)) {
    return { ok: false, error: 'Consent times are missing or out of range' }
  }
  if (!Array.isArray(input.peoplePresent) || input.peoplePresent.length === 0) {
    return { ok: false, error: 'List who is present' }
  }
  if (input.peoplePresent.length > SESSION_RECORDING_MAX_PEOPLE_PRESENT) {
    return { ok: false, error: `At most ${SESSION_RECORDING_MAX_PEOPLE_PRESENT} people present` }
  }
  const peoplePresent: SessionRecordingPersonPresent[] = []
  for (const person of input.peoplePresent) {
    const role = (person as { role?: unknown } | null)?.role
    if (!SESSION_RECORDING_PERSON_ROLES.includes(role as SessionRecordingPersonRole)) {
      return { ok: false, error: 'Unknown role for a person present' }
    }
    peoplePresent.push({
      role: role as SessionRecordingPersonRole,
      firstName: cleanFirstName((person as { firstName?: unknown }).firstName),
    })
  }
  if (!peoplePresent.some((p) => p.role === 'participant')) {
    return { ok: false, error: 'The participant must be listed as present' }
  }
  if (
    consentedBy !== 'participant' &&
    consentedBy !== 'authorised_person' &&
    !peoplePresent.some((p) => p.role === consentedBy)
  ) {
    return { ok: false, error: `The ${consentedBy} who agreed must be listed as present` }
  }

  const value: SessionRecordingConsentInput = {
    noticeVersion,
    consentedBy,
    method,
    participantAgreedAt: input.participantAgreedAt as string,
    providerConfirmedEveryoneAgreed: true,
    providerConfirmedAt: input.providerConfirmedAt as string,
    peoplePresent,
  }
  if (method === 'verbal_clip') {
    const verbal = validateVerbal(input.verbal)
    if (typeof verbal === 'string') return { ok: false, error: verbal }
    value.verbal = verbal
  } else if (method === 'nonverbal') {
    if (consentedBy !== 'participant') {
      return { ok: false, error: 'Showing agreement without words is for the participant themselves' }
    }
    const nonverbal = validateNonverbal(input.nonverbal)
    if (typeof nonverbal === 'string') return { ok: false, error: nonverbal }
    value.nonverbal = nonverbal
  } else if (method === 'guardian_app') {
    const requestId = (input.guardianApp as { requestId?: unknown } | null | undefined)?.requestId
    if (typeof requestId !== 'string' || !CLIP_ID.test(requestId)) {
      return { ok: false, error: 'Ask the person who can give consent in their app' }
    }
    value.guardianApp = { requestId }
  }

  // Only builds that ask "under 18?" can send the new methods; older tap-only builds send no age.
  if (input.age === undefined && method !== 'participant_tap') {
    return { ok: false, error: 'Say whether the participant is under 18' }
  }
  if (input.age !== undefined) {
    const age = input.age as Record<string, unknown> | null
    const source = age?.source
    if (typeof age?.under18 !== 'boolean' || (source !== 'date_of_birth' && source !== 'provider_answer')) {
      return { ok: false, error: 'Say whether the participant is under 18' }
    }
    value.age = { under18: age.under18, source }
  }
  if (value.age?.under18) {
    // In person: the parent/guardian says it on a clip. In an app: someone marked "Can give consent".
    if (consentedBy !== 'guardian' && consentedBy !== 'authorised_person') {
      return { ok: false, error: 'For someone under 18, their parent or guardian must agree' }
    }
    if (consentedBy === 'guardian' && method !== 'verbal_clip') {
      return { ok: false, error: 'For someone under 18, record their parent or guardian saying they consent' }
    }
    const assent = validateChildAssent(input.childAssent, nowMs)
    if (typeof assent === 'string') return { ok: false, error: assent }
    if (assent.method === 'verbal_clip' && assent.verbal.clipId === value.verbal?.clipId) {
      return { ok: false, error: 'Record the child’s answer separately' }
    }
    value.childAssent = assent
  }
  return { ok: true, value }
}

/** Who stopped the recording because consent was withdrawn. */
export type SessionRecordingWithdrawnBy =
  | 'participant'
  | 'guardian'
  | 'nominee'
  | 'other_person_present'
  | 'provider'

// --- Capture events (content-free audit) --------------------------------------------

export type SessionRecordingEventType =
  | 'paused'
  | 'resumed'
  | 'interrupted'
  | 'stopped'
  | 'cap_reached'

export type SessionRecordingEventReason =
  | 'private_moment'
  | 'someone_joined'
  | 'phone_call'
  | 'ascend_call'
  | 'system_interruption'
  | 'route_change'
  | 'low_storage'
  | 'mic_lost'
  | 'app_restarted'
  | 'finished'

export interface SessionRecordingEventInput {
  type: SessionRecordingEventType
  /** Device time (ISO). */
  at: string
  /** Seconds of audio captured so far (excludes pauses). */
  elapsedSec: number
  reason?: SessionRecordingEventReason
  /**
   * Only on `resumed` after a `someone_joined` pause: the person who joined,
   * whom the provider confirmed was told and agrees. Appended to the stored
   * consent record's `peoplePresent`.
   */
  personJoined?: SessionRecordingPersonPresent
}

// --- Transcript ------------------------------------------------------------------------

export interface SessionRecordingTranscriptTurn {
  /** Seconds from the start of the audio. */
  startSec: number
  /** 1-based neutral speaker number ("Speaker 1"); the provider names them in review. */
  speaker: number
  text: string
}

export interface SessionRecordingTranscript {
  turns: SessionRecordingTranscriptTurn[]
  speakerCount: number
  durationSec: number
  wordCount: number
}

/** `[mm:ss]` (minutes may exceed 59 — sessions run up to 100 minutes). */
export function formatTranscriptTimestamp(totalSec: number): string {
  const safe = Math.max(0, Math.floor(totalSec))
  const minutes = Math.floor(safe / 60)
  const seconds = safe % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

/** `[mm:ss] Speaker N: text` lines — the exact format the note prompts expect. */
export function transcriptToPromptText(transcript: Pick<SessionRecordingTranscript, 'turns'>): string {
  return transcript.turns
    .map((turn) => `[${formatTranscriptTimestamp(turn.startSec)}] Speaker ${turn.speaker}: ${turn.text}`)
    .join('\n')
}

// --- Note ---------------------------------------------------------------------------------

export type TherapyNoteSectionKey =
  | 'goals_addressed'
  | 'participant_carer_report'
  | 'objective_observations'
  | 'interventions'
  | 'response_progress'
  | 'risks_safety'
  | 'plan_followups'

export const THERAPY_NOTE_SECTIONS: ReadonlyArray<{
  key: TherapyNoteSectionKey
  label: string
  /** Guidance shown to the model (prompt) and as helper text in review. */
  description: string
}> = [
  {
    key: 'goals_addressed',
    label: 'Goals addressed',
    description: 'Goals addressed (as stated in the session or the plan goals supplied in CONTEXT)',
  },
  {
    key: 'participant_carer_report',
    label: 'Participant / carer report',
    description: 'Participant / carer report (subjective: what they said; short quotes allowed)',
  },
  {
    key: 'objective_observations',
    label: 'Objective observations & measures',
    description:
      'Objective observations & measures (observable behaviour, task performance, assistance level, counts, durations, scores actually stated)',
  },
  {
    key: 'interventions',
    label: 'Interventions & strategies',
    description: 'Interventions & strategies delivered (what the therapist actually did/taught)',
  },
  {
    key: 'response_progress',
    label: 'Response & progress toward goals',
    description: 'Response & progress toward goals (only outcomes evidenced in the session)',
  },
  {
    key: 'risks_safety',
    label: 'Risks, safety & safeguarding',
    description: 'Risks, safety & safeguarding (close to verbatim)',
  },
  {
    key: 'plan_followups',
    label: 'Plan, home program & follow-ups',
    description:
      'Plan, home program & follow-ups (ONLY items the therapist or participant explicitly stated)',
  },
]

export type TherapyNoteDetailLevel = 'brief' | 'standard' | 'detailed'

export interface TherapyNoteEvidence {
  quote: string
  /** `mm:ss` of the turn the quote came from. */
  ts: string
}

/**
 * Amber "check this" flags. `quote_not_found` / `no_evidence` come from the
 * deterministic server quote check; the rest from the second (verify) pass.
 */
export type TherapyNoteFlagKind =
  | 'quote_not_found'
  | 'no_evidence'
  | 'unsupported'
  | 'overstated'
  | 'misattributed'
  | 'recommendation_added'
  | 'off_topic'

export interface TherapyNoteFlag {
  kind: TherapyNoteFlagKind
  /** One sentence; may quote the transcript (shown to the provider, never logged). */
  detail: string
}

export interface TherapyNoteStatement {
  id: string
  section: TherapyNoteSectionKey
  text: string
  source: 'transcript' | 'context'
  evidence: TherapyNoteEvidence[]
  flags: TherapyNoteFlag[]
}

export interface SessionRecordingNote {
  /** Optimistic-concurrency version; bumps on every save/regenerate. */
  version: number
  detail: TherapyNoteDetailLevel
  sessionPrep: string
  statements: TherapyNoteStatement[]
  notDiscussed: TherapyNoteSectionKey[]
  /** Verify-pass omissions: important facts the draft missed (one line each). */
  omissions: string[]
  /**
   * The 5 legacy AHP fields. On `ahp-note-v1` drafts they are derived from
   * `ahp` (what Android ≤ 132 / iOS ≤ 97 edit, approve and copy).
   */
  ahpFields: ProgressNoteDraftFields
  /** Quality-gate warnings (e.g. summary under 150 words). */
  qualityWarnings: string[]
  generatedAt: string
  modelId: string
  promptVersion: string
  /** True once the provider saved their own edits over the AI draft. */
  edited: boolean
  editedAt: string | null
  /** The AHP "Session notes" draft (`ahp-note-v1`); absent on older drafts. */
  ahp?: AhpNote | null
}

// --- API DTOs ---------------------------------------------------------------------------

export interface SessionRecordingSummary {
  recordingId: string
  sessionUuid: string
  status: SessionRecordingStatus
  captureState: SessionRecordingCaptureState
  createdAt: string
  updatedAt: string
  startedAt: string
  stoppedAt: string | null
  /** Audio seconds captured (excludes pauses); null until stopped. */
  durationSec: number | null
  failure: SessionRecordingFailure | null
  audio: SessionRecordingAudioStatus
  hasTranscript: boolean
  hasDraft: boolean
  approvedAt: string | null
  regenerationsLeft: number
}

export interface SessionRecordingAuditEvent {
  type: string
  at: string
  /** Device time for capture events, when supplied. */
  deviceAt?: string | null
  elapsedSec?: number | null
  reason?: string | null
}

export interface SessionRecordingDetail extends SessionRecordingSummary {
  consent: SessionRecordingConsentRecord | null
  /** Spoken-consent clips kept as the consent record (metadata only; play via `consent-audio`). */
  consentClips: SessionRecordingConsentClip[]
  transcript: SessionRecordingTranscript | null
  /** `{ "1": "Therapist", "2": "Sam" }` — provider-assigned speaker names. */
  speakerNames: Record<string, string>
  note: SessionRecordingNote | null
  /** Content-free audit trail (newest last). */
  events: SessionRecordingAuditEvent[]
}

/** Member / follower view: that the session was recorded, never the content. */
export type SessionRecordingMemberState =
  | 'recording'
  | 'paused'
  | 'processing'
  | 'complete'
  | 'stopped_consent_withdrawn'
  | 'discarded'

export interface SessionRecordingMemberStatus {
  recordingId: string
  state: SessionRecordingMemberState
  startedAt: string
  stoppedAt: string | null
  audioDeletedAt: string | null
  /** When unapproved audio will be deleted (`startedAt` + 7 days); null once gone. */
  audioDeleteDueAt: string | null
  /** How agreement was given (null for rows without a consent record). */
  consentMethod: SessionRecordingConsentMethod | null
  consentedBy: SessionRecordingConsenter | null
  /** A spoken-consent clip is still kept (false once deleted, e.g. consent withdrawn after approval). */
  consentClipKept: boolean
}

export function memberStateForRecording(
  record: Pick<SessionRecordingSummary, 'status' | 'captureState' | 'startedAt'>,
  nowMs: number,
): SessionRecordingMemberState {
  if (record.status === 'withdrawn') return 'stopped_consent_withdrawn'
  if (record.status === 'discarded') return 'discarded'
  if (isSessionRecordingCaptureLive(record, nowMs)) {
    return record.captureState === 'paused' ? 'paused' : 'recording'
  }
  if (record.status === 'approved') return 'complete'
  return 'processing'
}

export interface CreateSessionRecordingRequest {
  consent: SessionRecordingConsentInput
}

/** `POST /sessions/{id}/recordings/consent-clip` — ask where to upload one spoken-consent clip. */
export interface RequestConsentClipUploadRequest {
  contentType: SessionRecordingConsentClipContentType
  sizeBytes: number
  durationSec: number
}

/** PUT the clip to `url` with exactly `contentType`, then send `clipId` in the consent. */
export interface ConsentClipUploadSlot {
  clipId: string
  url: string
  contentType: SessionRecordingConsentClipContentType
  expiresAt: string
}

/** `GET …/{recordingId}/consent-audio` — short-lived playback URLs for the clips still stored. */
export interface SessionRecordingConsentAudio {
  items: Array<{ clipId: string; purpose: SessionRecordingConsentClipPurpose; url: string }>
  expiresAt: string
}

export interface StartSessionRecordingUploadRequest {
  contentType: typeof SESSION_RECORDING_CONTENT_TYPE
  sizeBytes: number
  durationSec: number
}

export interface SessionRecordingUploadPart {
  partNumber: number
  /** Presigned UploadPart URL (absent when the part is already uploaded). */
  url: string | null
  /** ETag of an already-uploaded part (resume). */
  etag: string | null
}

export interface SessionRecordingUploadPlan {
  recordingId: string
  partSizeBytes: number
  parts: SessionRecordingUploadPart[]
  /** When the presigned part URLs expire; call `upload` again for fresh ones. */
  expiresAt: string
}

export interface CompleteSessionRecordingUploadRequest {
  parts: Array<{ partNumber: number; etag: string }>
}

/** Send `ahp` for `ahp-note-v1` drafts; older builds send the 5 `ahpFields`. */
export interface SaveSessionRecordingNoteRequest {
  expectedVersion: number
  ahpFields?: ProgressNoteDraftFields
  ahp?: AhpNoteEdits
  speakerNames?: Record<string, string>
}

export interface RegenerateSessionRecordingNoteRequest {
  detail: TherapyNoteDetailLevel
  /** Write the new draft for this AHP service (style guide + label). */
  service?: AhpService | null
}

export interface ApproveSessionRecordingRequest {
  expectedVersion: number
  /** "I've reviewed this note and it accurately reflects the session." Must be `true`. */
  attested: true
  /**
   * Sent by builds that show the AHP format: the server then also requires the
   * service, every GAS score and every Yes/No answer to be confirmed.
   */
  ahpConfirmed?: true
}

export interface WithdrawSessionRecordingRequest {
  by: SessionRecordingWithdrawnBy
}

export interface SessionRecordingAudioUrl {
  url: string
  expiresAt: string
}

/** `/sessions/{id}/recordings/{recordingId}/{action}` actions. */
export type SessionRecordingAction =
  | 'events'
  | 'upload'
  | 'complete'
  | 'note'
  | 'regenerate'
  | 'retry'
  | 'approve'
  | 'withdraw'
  | 'audio'
  | 'consent-audio'
  | 'places'

const enc = encodeURIComponent

export const sessionRecordingPaths = {
  /** GET (provider list) · POST (create with consent). */
  collection: (sessionUuid: string) => `/sessions/${enc(sessionUuid)}/recordings`,
  /** GET — content-free status for the member / followers / provider. */
  memberStatus: (sessionUuid: string) => `/sessions/${enc(sessionUuid)}/recordings/status`,
  /** POST — an upload slot for one spoken-consent clip (before create). */
  consentClip: (sessionUuid: string) => `/sessions/${enc(sessionUuid)}/recordings/consent-clip`,
  /** GET (who can be asked + requests, or one `?requestId=`) · POST (ask / answer). */
  consentRequests: (sessionUuid: string, requestId?: string) =>
    `/sessions/${enc(sessionUuid)}/recordings/consent-requests${requestId ? `?requestId=${enc(requestId)}` : ''}`,
  /** GET (detail) · DELETE (discard before approval). */
  one: (sessionUuid: string, recordingId: string) =>
    `/sessions/${enc(sessionUuid)}/recordings/${enc(recordingId)}`,
  /** POST events|upload|complete|regenerate|retry|approve|withdraw|places · PUT note · GET audio|consent-audio. */
  action: (sessionUuid: string, recordingId: string, action: SessionRecordingAction) =>
    `/sessions/${enc(sessionUuid)}/recordings/${enc(recordingId)}/${action}`,
}

export const SESSION_RECORDING_ERROR_CODES = {
  premiumRequired: 'PREMIUM_SESSION_RECORDING_REQUIRED',
  forbidden: 'FORBIDDEN',
  sessionNotFound: 'SESSION_NOT_FOUND',
  notFound: 'RECORDING_NOT_FOUND',
  notInPerson: 'NOT_IN_PERSON',
  notInProgress: 'SESSION_NOT_IN_PROGRESS',
  alreadyActive: 'RECORDING_ALREADY_ACTIVE',
  perSessionLimit: 'SESSION_RECORDING_LIMIT',
  dailyLimit: 'DAILY_LIMIT_REACHED',
  invalidConsent: 'INVALID_CONSENT',
  invalidRequest: 'INVALID_REQUEST',
  invalidState: 'INVALID_STATE',
  tooLarge: 'RECORDING_TOO_LARGE',
  versionConflict: 'NOTE_VERSION_CONFLICT',
  notReadyToApprove: 'NOT_READY_TO_APPROVE',
  regenerateLimit: 'REGENERATE_LIMIT_REACHED',
  retryLimit: 'RETRY_LIMIT_REACHED',
  audioGone: 'AUDIO_DELETED',
  consentRequestLimit: 'CONSENT_REQUEST_LIMIT',
} as const

export type SessionRecordingErrorCode =
  (typeof SESSION_RECORDING_ERROR_CODES)[keyof typeof SESSION_RECORDING_ERROR_CODES]

/** Error body every route returns: `{ error, message }` (message is safe to show). */
export interface SessionRecordingErrorBody {
  error: SessionRecordingErrorCode
  message: string
}

// --- Deterministic record text ---------------------------------------------------------

const CONSENTER_LABEL: Record<SessionRecordingConsenter, string> = {
  participant: 'the participant',
  guardian: 'the participant’s guardian',
  nominee: 'the participant’s nominee',
  authorised_person: 'a person authorised to give consent for the participant',
}

function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1)
}

function signPhrase(nonverbal: Pick<SessionRecordingNonverbalConsent, 'sign' | 'otherSign'>): string {
  if (nonverbal.sign === 'other' && nonverbal.otherSign) return `another way: ${nonverbal.otherSign}`
  return lowerFirst(SESSION_RECORDING_NONVERBAL_SIGN_LABELS[nonverbal.sign])
}

function spokenPhrase(verbal: Pick<SessionRecordingVerbalConsent, 'promptVersion'> | undefined): string {
  const version = verbal && isSessionRecordingVerbalPromptVersion(verbal.promptVersion)
    ? verbal.promptVersion
    : SESSION_RECORDING_VERBAL_PROMPT_VERSION
  return sessionRecordingVerbalPrompt(version).phrase.replace(/\.$/, '')
}

type StatementConsent = Pick<SessionRecordingConsentInput, 'consentedBy' | 'participantAgreedAt' | 'peoplePresent'> &
  Partial<Pick<SessionRecordingConsentInput, 'method' | 'verbal' | 'nonverbal' | 'guardianApp' | 'childAssent'>>

function agreementPhrase(consent: StatementConsent, time: string, clipsDeleted: boolean): string {
  if (consent.method === 'guardian_app') {
    const relationship = consentRelationshipLabel(consent.guardianApp?.relationship, consent.guardianApp?.relationshipName)
    return `${relationship ? `${relationship}, ` : ''}agreed in their Ascend app, ${time}`
  }
  if (consent.method === 'verbal_clip') {
    const clip = clipsDeleted ? 'the short consent clip has since been deleted' : 'kept as a short consent clip'
    return `said “${spokenPhrase(consent.verbal)}”, ${time} — ${clip}`
  }
  if (consent.method === 'nonverbal') {
    return consent.nonverbal
      ? `showed they agree without words (${signPhrase(consent.nonverbal)}), ${time}`
      : `showed they agree without words, ${time}`
  }
  return `tapped “Yes, record”, ${time}`
}

function assentSentence(assent: SessionRecordingChildAssent, clipsDeleted: boolean): string {
  switch (assent.method) {
    case 'participant_tap':
      return 'The participant is under 18 and also tapped “Yes, record”.'
    case 'verbal_clip':
      return clipsDeleted
        ? 'The participant is under 18 and also said yes — that clip has since been deleted.'
        : 'The participant is under 18 and also said yes — kept as a short consent clip.'
    case 'nonverbal':
      return `The participant is under 18 and also showed they agree without words (${signPhrase(assent.nonverbal)}).`
    case 'unable': {
      const { reason, detail } = assent.unable
      const why = reason === 'other' && detail ? `another reason: ${detail}` : lowerFirst(SESSION_RECORDING_ASSENT_UNABLE_LABELS[reason])
      return `The participant is under 18 and could not show their own agreement (${why}).`
    }
  }
}

/**
 * The "Recording & AI statement" appended when the approved note is copied
 * to AHP — deterministic, never AI-written.
 */
export function sessionRecordingStatement(input: {
  consent: StatementConsent
  providerName: string
  approvedAt: string | null
  formatTime: (iso: string) => string
  /** The spoken-consent clips were deleted (e.g. consent withdrawn after approval). */
  consentClipsDeleted?: boolean
}): string {
  const clipsDeleted = input.consentClipsDeleted === true
  const others = input.consent.peoplePresent.filter((p) => p.role !== 'participant').length
  const who = CONSENTER_LABEL[input.consent.consentedBy]
  const everyone = others > 0 ? ` and everyone present (${others + 1} people)` : ''
  const reviewed = input.approvedAt
    ? `reviewed and approved by ${input.providerName} on ${input.formatTime(input.approvedAt)}`
    : `to be reviewed and approved by ${input.providerName}`
  const agreement = agreementPhrase(input.consent, input.formatTime(input.consent.participantAgreedAt), clipsDeleted)
  const child = input.consent.childAssent ? ` ${assentSentence(input.consent.childAssent, clipsDeleted)}` : ''
  return (
    `Audio recorded with the consent of ${who}${everyone} (${agreement}); note drafted by AI from the ` +
    `transcript and ${reviewed}.${child} Audio deleted after approval.`
  )
}
